import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Modal} from "../../../../../components/modal/modal.jsx";
import {useQuery} from "@tanstack/react-query";
import corporationService from "../../../../../services/web/corporation-service.js";
import {APIProvider, ControlPosition, Map, Marker, useMap, useMapsLibrary} from "@vis.gl/react-google-maps";
import Button from "../../../../../components/button/button.jsx";
import html2canvas from "html2canvas";
import {v4} from "uuid";
import {debounce} from "lodash";

const Rectangle = ({ lat, lng, isMarker, open }) => {
    const map = useMap();
    const [polygon, setPolygon] = useState(undefined);

    const mapLib = useMapsLibrary("maps");

    useEffect(() => {
        if (!mapLib) return;

        if (isMarker || !open) {
            polygon?.setMap(null)
            setPolygon(undefined);
            return;
        }

        const zoneCoords = [
            {lat: lat - 0.0007, lng: lng + 0.0007},
            {lat: lat + 0.0007, lng: lng + 0.0007},
            {lat: lat + 0.0007, lng: lng - 0.0007},
            {lat: lat - 0.0007, lng: lng - 0.0007},
        ];

        setPolygon(new mapLib.Polygon({
            paths: zoneCoords,
            strokeColor: "#FF0000",
            strokeOpacity: 0.8,
            strokeWeight: 3,
            fillOpacity: 0.0,
            editable: true,
            draggable: true,
            map
        }));
    }, [mapLib, isMarker, open]);
}

const BoFormCroquiMap = ({ open, setOpen, append }) => {
    const [isMarker, setIsMarker] = useState(true);
    const mapRef = useRef(undefined);

    useEffect(() => {
        setIsMarker(true);
    }, [open]);

    const {data: corporation} = useQuery({
        queryFn: () => corporationService.entity(),
        queryKey: ["corporation"]
    });

    const lat = parseFloat(corporation?.latitude) || 0;
    const lng = parseFloat(corporation?.longitude) || 0;

    const snapshot = () => {
        document.querySelector(".gm-fullscreen-control").classList.add("!hidden");
        document.querySelectorAll(".gmnoprint").forEach(element => element.classList.add("!hidden"));
        document.querySelectorAll(".gm-style-iw-tc").forEach(element => element.classList.add("!hidden"));
        document.querySelectorAll(".gm-style-iw").forEach(element => element.classList.add("!hidden"));
        document.querySelectorAll(".gm-style-cc").forEach(element => element.classList.add("!hidden"));

        html2canvas(mapRef.current, {
            useCORS: true,
            allowTaint: true,
            logging: false,
            scrollX: 0,
            scrollY: 0
        }).then(function (canvas) {
            const dataURL = canvas.toDataURL();

            const attachId = v4();
            const file = dataURLtoFile(dataURL, attachId + ".png");

            const dataForModal = {
                name: file.name,
                type: file.type,
                extension: file.type,
                url: dataURL,
                file: file,
                attachId: attachId,
                itemId: attachId,
                description: ""
            };

            append(dataForModal);
            setOpen(false);

            setTimeout(() => {
                document.querySelector(".gm-fullscreen-control").classList.remove("!hidden");
                document.querySelectorAll(".gmnoprint").forEach(element => element.classList.remove("!hidden"));
                document.querySelectorAll(".gm-style-iw-tc").forEach(element => element.classList.remove("!hidden"));
                document.querySelectorAll(".gm-style-iw").forEach(element => element.classList.remove("!hidden"));
                document.querySelectorAll(".gm-style-cc").forEach(element => element.classList.remove("!hidden"));
            }, 200)
        });
    }

    const debouncedSnapshot = useMemo(
        () => debounce(snapshot, 1000, { leading: true, trailing: false }),
        []
    );

    const dataURLtoFile = (dataURL, filename) => {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];

        const bstr = atob(arr[1]);

        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime });
    };

    return (
        <Modal open={open} setOpen={setOpen} title={"Mapa"} className={"w-[800px]"}>
            <Modal.Body className={"!pt-0"}>
                <div ref={mapRef}>
                    <APIProvider apiKey={corporation?.maps_api_key}>
                        <Map
                            reuseMaps={true}
                            defaultZoom={15}
                            mapTypeId={"hybrid"}
                            streetViewControl={false}
                            defaultCenter={{lat,lng}}
                            gestureHandling={'greedy'}
                            style={{ height: "calc(-250px + 100vh)", width: "100%" }}
                            mapTypeControlOptions={{position: ControlPosition.TOP_RIGHT}}
                            fullscreenControlOptions={{position: ControlPosition.BOTTOM_RIGHT}}
                        >
                            {
                                isMarker &&
                                <Marker
                                    position={{lat,lng}}
                                    draggable={true}
                                />
                            }

                            <Rectangle
                                open={open}
                                isMarker={isMarker}
                                lat={lat}
                                lng={lng}
                            />
                        </Map>
                    </APIProvider>
                </div>
            </Modal.Body>

            <Modal.Footer className={"!justify-between"}>
                <section className={"flex"}>
                    <Button target={isMarker ? "primary" : "default"} className={"!rounded-r-none"} onClick={() => setIsMarker(true)}>
                        Ponto
                    </Button>
                    <Button target={!isMarker ? "primary" : "default"} className={"!rounded-l-none"} onClick={() => setIsMarker(false)}>
                        Área
                    </Button>
                </section>

                <Button onClick={debouncedSnapshot}>
                    Confirmar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BoFormCroquiMap;